import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import {
  DEFAULT_MENTOR_LANGUAGE,
  MENTOR_LANGUAGE_OPTIONS,
} from "@/lib/mentor-profile/constants";
import { cn } from "@/lib/utils";
import { TextInput } from "@somethings-ui/web";
import { useState } from "react";
import { Languages } from "lucide-react";
import { WizardStepLayout } from "./wizard-step-layout";
import type { WizardStepProps } from "./types";

const PILL_BASE =
  "rounded-full border border-[#e7e0d5] px-[17px] py-[9px] font-body text-[15px] leading-[1.4] transition-colors";

export function LanguagesStep({
  draft,
  saveState,
  setField,
  embedded,
  reviewerNotes,
  onContinue,
  onBack,
}: WizardStepProps) {
  const [showAddInput, setShowAddInput] = useState(false);
  const [customLanguage, setCustomLanguage] = useState("");

  const selected =
    draft.languages && draft.languages.length > 0
      ? draft.languages
      : [DEFAULT_MENTOR_LANGUAGE];

  const options = [
    ...MENTOR_LANGUAGE_OPTIONS,
    ...selected.filter(
      (language) =>
        !MENTOR_LANGUAGE_OPTIONS.includes(
          language as (typeof MENTOR_LANGUAGE_OPTIONS)[number],
        ),
    ),
  ];

  const toggle = (language: string) => {
    if (language === DEFAULT_MENTOR_LANGUAGE) return;
    const next = selected.includes(language)
      ? selected.filter((l) => l !== language)
      : [...selected, language];
    setField("languages", next);
  };

  const addCustomLanguage = () => {
    const value = customLanguage.trim();
    if (value && !selected.includes(value)) {
      setField("languages", [...selected, value]);
    }
    setCustomLanguage("");
    setShowAddInput(false);
  };

  return (
    <WizardStepLayout
      reviewerNotes={reviewerNotes}
      embedded={embedded}
      stepIndex={3}
      icon={<Languages className="size-7" />}
      heading="What languages can you mentor in?"
      subheading="We'll match you with teens you can talk to comfortably."
      saveState={saveState}
      onBack={onBack}
      onContinue={onContinue}
      preview={
        <MatchingProfilePreviewCard
          draft={draft}
          focus={{ region: "languages", zoom: 1 }}
        />
      }
    >
      <div className="flex flex-wrap gap-2">
        {options.map((language) => {
          const isSelected = selected.includes(language);
          return (
            <button
              key={language}
              type="button"
              onClick={() => toggle(language)}
              className={cn(
                PILL_BASE,
                isSelected
                  ? "bg-[#1f1e1d] text-white"
                  : "bg-white text-[#1f1e1d] hover:bg-[#faf8f5]",
              )}
            >
              {language}
            </button>
          );
        })}
        {showAddInput ? (
          <TextInput
            variant="standard"
            autoFocus
            placeholder="Add a language"
            value={customLanguage}
            onChange={(e) => setCustomLanguage(e.target.value)}
            onBlur={addCustomLanguage}
            onKeyDown={(e) => e.key === "Enter" && addCustomLanguage()}
            className="w-40"
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowAddInput(true)}
            className={cn(PILL_BASE, "bg-white text-[#1f1e1d] hover:bg-[#faf8f5]")}
          >
            + Add another
          </button>
        )}
      </div>
      <p className="font-body text-[13px] text-[#797774]">
        Only used to match you with teens — this isn&apos;t shown on your
        profile.
      </p>
    </WizardStepLayout>
  );
}
